import React, { useState, useContext, useEffect } from "react";
import { AuthContext, API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { toast } from "sonner";
import { Shield, ArrowLeft } from "lucide-react";
import { DemoSignupModal } from "./demo/DemoSignupModal";
import { PasswordInput } from "./PasswordInput";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { trackHomepage, trackLogin } from "../lib/visitorTracking";

export default function Login() {
  const { login } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [showDemoSignup, setShowDemoSignup] = useState(false);

  // Anonymous visitor tracking: homepage view (skipped if already logged in)
  useEffect(() => { trackHomepage(); }, []);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("expired")) {
      setTimeout(() => toast.info("Your password was changed. Please sign in again."), 300);
      window.history.replaceState(null, "", "/login");
    }
  }, []);
  
  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });

  // First-login set-password (seeded Owner/Platform-Admin accounts)
  const [setupAccount, setSetupAccount] = useState(null); // {user_id, email, username}
  const [pwd, setPwd] = useState({ a: "", b: "" });
  const [showForgot, setShowForgot] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginData.email, password: loginData.password })
      });
      const data = await response.json();

      if (response.ok && data.must_set_password) {
        setSetupAccount(data);
        toast.info("First login — please set your password.");
      } else if (response.ok) {
        trackLogin();
        login(data.token, data.user);
        toast.success("Welcome back! Logged in successfully.");
      } else {
        toast.error(data.detail || "Login failed");
      }
    } catch (error) {
      toast.error("Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (pwd.a.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pwd.a !== pwd.b) { toast.error("Passwords do not match"); return; }
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/auth/set-initial-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: setupAccount.user_id, new_password: pwd.a })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Password set! Logging you in...");
        login(data.token, data.user);
      } else {
        toast.error(data.detail || "Could not set password");
      }
    } catch {
      toast.error("Could not set password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50 px-4">
      <div className="w-full max-w-md">
        {/* Back to public site */}
        <div className="mb-4">
          <a
            href="/"
            data-testid="login-back-to-site"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-amber-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to site
          </a>
        </div>
        {/* Header */}
        <div className="text-center mb-8 fade-in-up">
          <div className="flex items-center justify-center mb-4">
            <img src="/logo.png?v=3" alt="Site Pro App Logo" className="w-20 h-20 object-contain" />
          </div>
          <h1 className="heading-primary text-3xl mb-2">Site Pro App</h1>
          <p className="text-slate-600">Daily site management made simple</p>
        </div>

        <Card className="glass-morphism border-amber-200 shadow-xl hover-lift">
          <CardHeader className="text-center pb-4">
            <CardTitle className="heading-secondary text-xl text-construction-accent">
              Access Your Dashboard
            </CardTitle>
          </CardHeader>
          
          <CardContent>
            {setupAccount ? (
              <form onSubmit={handleSetPassword} className="space-y-4" data-testid="set-password-form">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-slate-700">
                  Welcome <span className="font-semibold">{setupAccount.username || setupAccount.email}</span> — set a password to activate your account.
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="font-medium text-slate-700">New Password</Label>
                  <PasswordInput id="new-password" placeholder="At least 6 characters"
                    value={pwd.a} onChange={(e) => setPwd({ ...pwd, a: e.target.value })} required
                    className="border-amber-200 focus:border-amber-400" data-testid="new-password-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="font-medium text-slate-700">Confirm Password</Label>
                  <PasswordInput id="confirm-password" placeholder="Re-enter your password"
                    value={pwd.b} onChange={(e) => setPwd({ ...pwd, b: e.target.value })} required
                    className="border-amber-200 focus:border-amber-400" data-testid="confirm-password-input" />
                </div>
                <Button type="submit" className="w-full btn-construction h-11 font-semibold"
                  disabled={isLoading} data-testid="set-password-submit-button">
                  {isLoading ? "Saving..." : "Set Password & Sign In"}
                </Button>
              </form>
            ) : showForgot ? (
              <ForgotPasswordForm initialEmail={loginData.email} onBack={() => setShowForgot(false)} />
            ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="font-medium text-slate-700">
                  Email Address
                </Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="manager@construction.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                  required
                  className="border-amber-200 focus:border-amber-400"
                  data-testid="login-email-input"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="login-password" className="font-medium text-slate-700">
                    Password
                  </Label>
                  <button type="button" onClick={() => setShowForgot(true)}
                    className="text-sm font-medium text-amber-700 hover:text-amber-800 underline-offset-2 hover:underline"
                    data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </div>
                <PasswordInput
                  id="login-password"
                  placeholder="Enter your password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  required
                  className="border-amber-200 focus:border-amber-400"
                  data-testid="login-password-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full btn-construction h-11 font-semibold"
                disabled={isLoading}
                data-testid="login-submit-button"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Signing In...</span>
                  </div>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    Sign In to Dashboard
                  </>
                )}
              </Button>
            </form>
            )}
            
            <div className="mt-4 text-center">
              <p className="text-sm text-slate-600">
                Need an account? Contact your administrator.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Demo / free-trial block */}
        <div className="my-3 border-t border-amber-300" />
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 shadow-sm" data-testid="demo-block">
          <span className="inline-block bg-amber-500 text-slate-900 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full">
            DEMO
          </span>
          <h3 className="text-base font-bold text-slate-900 mt-1.5">Try SiteProApp free for 30 days</h3>
          <p className="text-sm text-slate-600 mt-0.5">Full access. One site. No credit card. Cancel anytime.</p>
          <Button
            onClick={() => setShowDemoSignup(true)}
            className="w-full mt-2.5 h-10 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            data-testid="start-free-trial-btn"
          >
            Start Free Trial
          </Button>
          <p className="text-[11px] text-slate-400 mt-1.5 text-center">
            Requests are reviewed and approved within 15 minutes during business hours
          </p>
          <div className="text-center mt-2">
            <a href="/pricing" className="text-sm font-medium text-amber-700 hover:text-amber-800 underline underline-offset-2" data-testid="login-pricing-link">
              See plans &amp; pricing →
            </a>
          </div>
        </div>

        <footer className="mt-6 text-center text-xs text-slate-400 space-x-3">
          <a href="/pricing" className="hover:text-slate-600" data-testid="footer-pricing-link">Pricing</a>
          <span>·</span>
          <span>© {new Date().getFullYear()} SiteProApp</span>
        </footer>
      </div>

      <DemoSignupModal open={showDemoSignup} onClose={() => setShowDemoSignup(false)} />
    </div>
  );
}